import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  BookOpen, ChevronRight, ArrowLeft, Loader, Calendar, GraduationCap
} from 'lucide-react';

// Import Header
import Header from '../components/common/Header';
import { publicAPI } from '../services/api';

const YearSelection = () => {
  // 1. Get IDs from URL
  const { schoolId, departmentId } = useParams();
  const navigate = useNavigate();
  
  const [department, setDepartment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 2. Fetch Department Details
  useEffect(() => {
    const fetchDept = async () => {
      try {
        const response = await publicAPI.getDepartmentById(departmentId);
        if (response.success) {
          setDepartment(response.data);
        } else {
          setError('Department not found in database');
        }
      } catch (err) {
        console.error("Failed to load department", err);
        setError('Connection error or invalid ID');
      } finally {
        setLoading(false);
      }
    };
    fetchDept();
  }, [departmentId]);

  // Helper to get labels based on the year number
  const getYearInfo = (level) => {
    const map = {
      1: { label: 'First Year', desc: 'Freshman Courses' },
      2: { label: 'Second Year', desc: 'Sophomore Courses' },
      3: { label: 'Third Year', desc: 'Junior Courses' },
      4: { label: 'Fourth Year', desc: 'Senior Courses' },
      5: { label: 'Fifth Year', desc: 'Final Year / Thesis' },
      6: { label: 'Sixth Year', desc: 'Internship / Clinical' },
      7: { label: 'Seventh Year', desc: 'Final Rotation' }
    };
    return map[level] || { label: `Year ${level}`, desc: 'Advanced Studies' };
  };

  // Determine which years to show
  // Use activeYears from DB if available, otherwise default to 1-5
    const displayYears = department?.activeYears?.length > 0
    ? department.activeYears.sort((a, b) => a - b)
    : [];

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header added here */}
      <Header />

      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-blue-900 to-indigo-900 text-white py-12">
        <div className="container mx-auto px-4">
          <Link 
            to={`/school/${schoolId}`} 
            className="inline-flex items-center gap-2 text-blue-200 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to School
          </Link>
          
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/10 rounded-xl backdrop-blur-sm border border-white/20 animate-pulse">
              <GraduationCap className="w-10 h-10 text-blue-200" />
            </div>
            <div className="flex-1 w-full">
              {loading ? (
                <div className="animate-pulse space-y-2">
                  <div className="h-8 bg-white/20 rounded w-1/3"></div>
                  <div className="h-4 bg-white/15 rounded w-1/4"></div>
                </div>
              ) : (
                <>
                  <h1 className="text-3xl font-bold">{department?.name}</h1>
                  <p className="text-blue-200 text-sm mt-1">{department?.code} • Select Your Academic Year</p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Years Grid */}
      <div className="container mx-auto px-4 -mt-8">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 h-[190px] flex flex-col justify-between">
                <div>
                  <div className="flex justify-between items-start mb-4">
                    <div className="p-3 bg-gray-100 rounded-lg w-14 h-14"></div>
                    <div className="h-10 bg-gray-100 rounded w-10"></div>
                  </div>
                  <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-100 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center max-w-2xl mx-auto shadow-sm">
            <h3 className="text-xl font-bold text-red-800 mb-2">{error}</h3>
            <Link to={`/school/${schoolId}`} className="text-blue-600 hover:underline">
              ← Return to School Page
            </Link>
          </div>
        ) : displayYears.length === 0 ? (
          <div className="bg-white p-8 rounded-xl shadow-lg text-center">
            <p className="text-gray-500">No active academic years found for this department.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayYears.map((level) => {
              const { label, desc } = getYearInfo(level);
              return (
                <button
                  key={level}
                  // 3. Navigate to Course List with filters
                  onClick={() => navigate(`/school/${schoolId}/department/${departmentId}/year/${level}`)}
                  className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 hover:border-blue-500 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group text-left relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                    <Calendar className="w-24 h-24 text-blue-600" />
                  </div>
                  
                  <div className="flex justify-between items-start mb-4 relative z-10">
                    <div className="p-3 bg-blue-50 text-blue-600 rounded-lg group-hover:bg-blue-600 group-hover:text-white transition-colors">
                      <Calendar className="w-8 h-8" />
                    </div>
                    <span className="text-5xl font-bold text-gray-100 group-hover:text-blue-50 transition-colors">
                      0{level}
                    </span>
                  </div>
                  
                  <div className="relative z-10">
                    <h3 className="text-xl font-bold text-gray-900 mb-1 group-hover:text-blue-700 transition-colors">
                      {label}
                    </h3>
                    <p className="text-gray-500 text-sm mb-4">{desc}</p>
                    <div className="flex items-center text-blue-600 font-medium text-sm">
                      View Courses 
                      <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default YearSelection;